<!-- amazon-nova-pro.component.html -->
<div class="nova-dialog" role="dialog" aria-modal="true" aria-label="Amazon Nova Pro model details">
  <button
    class="close-btn"
    mat-icon-button
    (click)="close()"
    aria-label="Close Amazon Nova Lite Info"
  >
    ×
  </button>

  <!-- Header -->
  <div class="header">
    <img
      src="assets/logos/amazon.svg"
      alt="Amazon Nova Pro Logo"
      class="logo"
      loading="lazy"
      decoding="async"
    />

    <h2>Amazon Nova Pro</h2>

    <div class="sub">
      High-capability multimodal model for accurate reasoning, agents, and complex enterprise tasks
    </div>
  </div>

  <!-- Body -->
  <div class="body">
    <p>
      <strong>Amazon Nova Pro</strong> is AWS’s “do-more” Nova model — a
      <strong>high-capability, multimodal</strong> foundation model built to deliver a strong blend of
      <strong>accuracy</strong>, <strong>reasoning depth</strong>, and <strong>production reliability</strong>
      across demanding workflows.
      <br />
      Compared to Lite, Pro is usually the better pick when tasks involve
      <strong>complex decisions</strong>, <strong>multi-step planning</strong>, <strong>long documents</strong>,
      or <strong>visual understanding</strong> where mistakes are expensive.
    </p>

    <ul class="feature-list">
      <li>
        🧠 <strong>Dynamic “Extended Thinking”:</strong>
        Enable deeper reasoning only when needed (often with low/medium/high effort).
        Keep it off for speed on routine steps.
      </li>

      <li>
        🧩 <strong>Stronger Complex-Task Accuracy:</strong>
        Better at multi-hop questions, long-horizon plans, constraint satisfaction, and “why” / tradeoff analysis.
      </li>

      <li>
        🖼️🎥📄 <strong>Multimodal Understanding:</strong>
        Accepts <strong>text + images + video + documents</strong> (and can interpret charts/figures) —
        ideal for IDP, visual QA, and video comprehension.
      </li>

      <li>
        🧰 <strong>Agent &amp; Tool Use:</strong>
        Designed for <strong>structured outputs</strong> and dependable <strong>function calling</strong> —
        great for automations that must be deterministic.
      </li>

      <li>
        🧾 <strong>Long-Context Work:</strong>
        Built for analyzing large inputs (policies, contracts, KB dumps, transcripts) while maintaining coherence.
      </li>

      <li>
        🌐 <strong>Built-in Tools:</strong>
        Supports <strong>web grounding</strong> (retrieval + citations) and a <strong>code interpreter</strong>
        for executing / validating logic inside a workflow.
      </li>

      <li>
        🔐 <strong>Enterprise Guardrails:</strong>
        Responsible-AI controls and governance patterns suitable for regulated environments.
      </li>

      <li>
        🧱 <strong>Bedrock-Native:</strong>
        Available through <strong>Amazon Bedrock</strong> with AWS security, deployment controls,
        and (where enabled) cross-Region inference options.
      </li>
    </ul>

    <!-- Cards -->
    <div class="optimal">
      <h3>When is Amazon Nova Pro optimal?</h3>
      <ul>
        <li>When accuracy matters more than absolute lowest cost (risk, compliance, finance ops, policy-heavy decisions)</li>
        <li>For <strong>agentic workflows</strong>: planning → tool calls → verification → reporting</li>
        <li>For <strong>document intelligence</strong>: contracts, SOPs, security docs, legal/compliance review, large-scale summarization</li>
        <li>For <strong>multimodal QA</strong>: screenshots, diagrams, charts, product images, video clips</li>
        <li>When you want <strong>structured JSON</strong> outputs and stable schemas for downstream systems</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Recommended usage pattern (Pro in production)</h3>
      <ul>
        <li>Use <strong>Lite/Micro</strong> for routing, cheap drafts, or high-volume “easy” steps</li>
        <li>Escalate to <strong>Pro</strong> for decision points (ambiguity, contradictions, risk, multi-step planning)</li>
        <li>Use <strong>tool calls</strong> for any action that must be correct (search, DB reads, ticket creation, billing ops)</li>
        <li>Add <strong>verifiers</strong>: schema validation + rule checks + post-hoc self-check prompts</li>
        <li>Keep <strong>extended thinking</strong> gated behind heuristics (doc length, uncertainty score, “critical” labels)</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Where Pro tends to shine</h3>
      <ul>
        <li>🔎 <strong>Root-cause analysis:</strong> correlating logs, timelines, and change history into a plausible causal story</li>
        <li>🧠 <strong>Decision support:</strong> weighing constraints, identifying tradeoffs, proposing safe defaults</li>
        <li>📚 <strong>Policy-aware assistants:</strong> long context + citations + consistent, auditable outputs</li>
        <li>🧑‍💻 <strong>Engineering copilots:</strong> reading larger code contexts, generating patches, and verifying with tools/tests</li>
      </ul>
    </div>
  </div>
</div>
